import BaseValidator from '~/composer/composer/lib/validation/BaseValidator';
import ValidationFail from '~/composer/composer/lib/validation/ValidationFail';
import ValidationSuccess from '~/composer/composer/lib/validation/ValidationSuccess';
import ValidationResult from '~/composer/composer/lib/validation/ValidationResult';
import twitterText from 'twitter-text';
import {
  getMaxHashtagMessage,
  getMaxMentionsMessage,
  getReelsAspectRatioMessage,
  getReelsVideoTooLongMessage,
  getReelsVideoTooShortMessage,
  getVideoFrameRateMessage,
  getVideoTooLargeMessage,
  getVideoTooWideMessage,
  INVALID_LINK,
} from './utils/validationErrorMessages';

export default class InstagramValidator extends BaseValidator {
  protected isValidShopgridUrl() {
    const { shopgridLink } = this.draft;

    if (
      shopgridLink &&
      shopgridLink.replace(/\s+/g, '') !== '' &&
      !twitterText.isValidUrl(shopgridLink, true, false)
    ) {
      return new ValidationFail(INVALID_LINK);
    }

    return new ValidationSuccess();
  }

  protected maxHashtags() {
    if (
      this.draft.getNumberOfHashtags() >
      (this.draft.service.maxHashtags as number)
    ) {
      return new ValidationFail(
        getMaxHashtagMessage(this.draft.service.maxHashtags as number)
      );
    }
    return new ValidationSuccess();
  }

  protected maxMentions() {
    if (
      this.draft.getNumberOfMentions() > (this.draft.service.maxMentions ?? 0)
    ) {
      return new ValidationFail(
        getMaxMentionsMessage(this.draft.service.maxMentions as number)
      );
    }
    return new ValidationSuccess();
  }

  protected maxCharactersInComment() {
    if (
      (this.draft.characterCommentCount ?? 0) >
      (this.draft.service.commentCharLimit ?? 0)
    ) {
      return new ValidationFail(
        `We can only fit ${this.draft.service.commentCharLimit} characters for comments`
      );
    }
    return new ValidationSuccess();
  }

  protected isAspectRatioIncorrectForVideo(
    minAspectRatio: number,
    maxAspectRatio: number
  ) {
    if (this.draft.video !== null) {
      const aspectRatio = this.draft.video.width / this.draft.video.height;
      return aspectRatio < minAspectRatio || aspectRatio > maxAspectRatio;
    }
    return false;
  }

  protected isVideoTooWide(maxWidth) {
    return this.draft.video !== null && this.draft.video.width > maxWidth;
  }

  protected isFrameRateIncorrectForVideo(
    minFrameRate: number,
    maxFrameRate: number
  ) {
    if (this.draft.video !== null && !!this.draft.video.frameRate) {
      const { frameRate } = this.draft.video;
      return frameRate < minFrameRate || frameRate > maxFrameRate;
    }
    return false;
  }

  protected isVideoTooLong(maxDuration) {
    return (
      this.draft.video !== null &&
      maxDuration &&
      this.draft.video.durationMs > maxDuration
    );
  }

  protected isVideoTooShort(minDuration) {
    return (
      this.draft.video !== null &&
      minDuration &&
      this.draft.video.durationMs < minDuration
    );
  }

  protected isVideoTooBig(maxSize) {
    return (
      this.draft.video !== null && maxSize && this.draft.video.size > maxSize
    );
  }

  protected validateVideo() {
    if (this.draft.isReelsPost()) {
      return this.validateReelsVideo();
    }

    if (this.draft.isStoryPost()) {
      return this.validateStoriesVideo();
    }

    return super.validateVideo();
  }

  protected validateReelsVideo() {
    if (
      this.isAspectRatioIncorrectForVideo(
        this.draft.service.videoMinAspectRatioReels as number,
        this.draft.service.videoMaxAspectRatioReels as number
      )
    ) {
      return new ValidationFail(
        getReelsAspectRatioMessage(
          this.draft.service.videoMinAspectRatioHumanReadableReels as string,
          this.draft.service.videoMaxAspectRatioHumanReadableReels as string
        )
      );
    }

    if (this.isVideoTooLong(this.draft.service.videoMaxDurationMsReels)) {
      return new ValidationFail(
        getReelsVideoTooLongMessage(
          (this.draft.service.videoMaxDurationMsReels ?? 0) / 1000
        )
      );
    }

    if (this.isVideoTooShort(this.draft.service.videoMinDurationMsReels)) {
      return new ValidationFail(
        getReelsVideoTooShortMessage(
          (this.draft.service.videoMinDurationMsReels ?? 0) / 1000
        )
      );
    }

    if (this.isVideoTooBig(this.draft.service.videoMaxSizeReels)) {
      const maxFileSizeInMb =
        (this.draft.service.videoMaxSizeReels ?? 0) / 1024 / 1024;
      return new ValidationFail(getVideoTooLargeMessage(maxFileSizeInMb));
    }

    if (this.isVideoTooWide(this.draft.service.videoMaxWidth)) {
      return new ValidationFail(
        getVideoTooWideMessage(
          this.draft.service.videoMaxWidth as number,
          this.draft.video?.width as number
        )
      );
    }

    if (
      this.isFrameRateIncorrectForVideo(
        this.draft.service.videoMinFrameRate as number,
        this.draft.service.videoMaxFrameRate as number
      )
    ) {
      return new ValidationFail(
        getVideoFrameRateMessage(
          this.draft.service.videoMinFrameRate as number,
          this.draft.service.videoMaxFrameRate as number,
          this.draft.video?.frameRate as number
        )
      );
    }

    return new ValidationSuccess();
  }

  protected validateStoriesVideo() {
    if (
      this.isAspectRatioIncorrectForVideo(
        this.draft.service.videoMinAspectRatioStories as number,
        this.draft.service.videoMaxAspectRatioStories as number
      )
    ) {
      return new ValidationFail(
        getReelsAspectRatioMessage(
          this.draft.service.videoMinAspectRatioHumanReadableStories as string,
          this.draft.service.videoMaxAspectRatioHumanReadableStories as string
        )
      );
    }

    if (this.isVideoTooWide(this.draft.service.videoMaxWidth)) {
      return new ValidationFail(
        getVideoTooWideMessage(
          this.draft.service.videoMaxWidth as number,
          this.draft.video?.width as number
        )
      );
    }

    if (this.isVideoTooLong(this.draft.service.videoMaxDurationMsStories)) {
      return new ValidationFail(
        getReelsVideoTooLongMessage(
          (this.draft.service.videoMaxDurationMsStories ?? 0) / 1000
        )
      );
    }

    if (
      this.isFrameRateIncorrectForVideo(
        this.draft.service.videoMinFrameRate as number,
        this.draft.service.videoMaxFrameRate as number
      )
    ) {
      return new ValidationFail(
        getVideoFrameRateMessage(
          this.draft.service.videoMinFrameRate as number,
          this.draft.service.videoMaxFrameRate as number,
          this.draft.video?.frameRate as number
        )
      );
    }

    if (this.isVideoTooShort(this.draft.service.videoMinDurationMsStories)) {
      return new ValidationFail(
        getReelsVideoTooShortMessage(
          (this.draft.service.videoMinDurationMsStories ?? 0) / 1000
        )
      );
    }

    if (this.isVideoTooBig(this.draft.service.videoMaxSizeStories)) {
      const maxFileSizeInMb =
        (this.draft.service.videoMaxSizeStories ?? 0) / 1024 / 1024;
      return new ValidationFail(getVideoTooLargeMessage(maxFileSizeInMb));
    }

    return new ValidationSuccess();
  }

  protected validateForChannel(): ValidationResult[] {
    return [
      this.isValidShopgridUrl(),
      this.maxHashtags(),
      this.maxMentions(),
      this.maxCharactersInComment(),
    ];
  }
}
